import { ChangeEvent, memo, FocusEvent } from "react"

import { CellProps, useFocusManagement } from "components/TreeState"
import { ChangedPlanRecord, PlanRecord, PlanRecordType } from "../types"
import { checkBuyingStatus } from "utils/type-guards"
import { formatFixedNumberCurrency, parseNumber } from "utils/number-format"
import { isSemanticReadonly, usePlanCachedData } from "../utils"

import { Bulky } from "./Bulky"
import { CellError } from "./CellError"
import { FieldsKey } from "../components/AutoCompute/types"
import { Info } from "./Info"
import Input from "ui/inputs/Input"
import { Tooltip } from "@material-ui/core"
import { css } from "@emotion/css"
import { get } from "lodash"
import { set } from "lodash/fp"
import { useAutoCompute } from "../components/AutoCompute/useAutoCompute"
import { useBillingTypesRules } from "../useBillingTypeRules"
import { useDefaultFieldIds } from "../useDefaultFieldIds"
import { useRifm } from "rifm"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { useUndoStack } from "../useUndoStack"
import { useAutocomputeValidation } from "../useAutocomputeValidation"
import { DEFAULT_PRECISION, usePrecision } from "providers/A12n"

export const PreciseTextCell = memo(function _PreciseTextCell(
  props: CellProps,
) {
  const precision = usePrecision()
  return <TextCell {...props} precision={precision} />
})

export const TextCell = memo(function _TextCell(props: CellProps) {
  const {
    rowId: id,
    dataKey,
    columnId,
    disabled,
    category,
    format,
    primaryKey,
    semantic,
    precision = DEFAULT_PRECISION,
  } = props
  const monetary = format === "MONETARY"
  const defaultFieldIds = useDefaultFieldIds(id)
  const autoCompute = useAutoCompute()
  const tableSource = useTableSource()
  const readFrom = useSnapshot(tableSource.state, { sync: true })
  const readError = useSnapshot(tableSource.errors)
  const globalUndoState = useUndoStack<ChangedPlanRecord>()
  const writeTo = tableSource.state

  const values = readFrom[id] || {}
  const { type, packaged } = values
  const isMediaSubmedia =
    type === "media_type_plan" || type === "media_subtype_plan"

  const buyingStatusVal = String(get(readFrom, `${id}.buyingStatus`))
  const buyingStatus = checkBuyingStatus(buyingStatusVal) ? buyingStatusVal : ""

  const mediaFields = readFrom[id]?.[
    primaryKey as keyof PlanRecord
  ] as PlanRecord["planningMediaFields"]

  const isReadonlySemantic = isSemanticReadonly(
    type as PlanRecordType,
    primaryKey as FieldsKey,
    semantic,
    packaged,
    defaultFieldIds,
    mediaFields,
  )

  const readonly =
    isReadonlySemantic || (isMediaSubmedia && category !== "SPOTS_PLAN")

  const isBooking = primaryKey === "buyingMediaFields"
  const planData = usePlanCachedData(isBooking)

  const action = readFrom[id]?.action
  const updatable = !action || action === "UPDATE_ORDER"
  const error = get(readError, `${id}.${columnId}`)
  const value = String(get(readFrom, `${id}.${dataKey}`) ?? "")

  const handleChange = (v: string) => {
    tableSource.clearError(id)
    globalUndoState.push([writeTo[id] as ChangedPlanRecord])
    writeTo[id] = set(dataKey, v, writeTo[id])
    updatable && (writeTo[id] = set("action", "UPDATE", writeTo[id]))
  }

  const handleBlur = (evt: FocusEvent<HTMLInputElement>) => {
    const { value } = evt.target
    hasChanged(value) && autoCompute?.compute(writeTo[id] as ChangedPlanRecord)
  }

  const rifm = useRifm({
    value: !!value ? `${value}${"0".repeat(precision)}` : "",
    onChange: (v) => handleChange(parseNumber(v)),
    format: (v) =>
      !!v
        ? formatFixedNumberCurrency(v, planData?.currencyCode, precision)
        : "",
  })

  const _v = monetary ? rifm.value : value

  const { handleFocus, hasChanged } = useAutocomputeValidation(_v, true)

  const _onChange = monetary
    ? rifm.onChange
    : (evt: ChangeEvent<HTMLInputElement>) => {
        handleChange(evt.target.value)
      }

  const { getInputProps } = useFocusManagement()

  // apply billing type rules
  const viewable = useBillingTypesRules(
    values as ChangedPlanRecord,
    primaryKey,
    semantic,
  )
  if (!viewable) return <span />

  const isDisabled = buyingStatus === "POSTED_FOR_BILLING"

  return (
    <div className={styles.wrapper}>
      <Bulky {...props} otherDisabled={readonly}>
        <div style={{ width: "100%" }}>
          <Tooltip title={_v} placement="bottom-start">
            <Input
              {...getInputProps()}
              id={`${id}/${dataKey}`}
              className={styles.input}
              value={_v}
              onFocus={handleFocus}
              onChange={_onChange}
              disabled={isDisabled}
              type="text"
              readOnly={disabled || readonly}
              onBlur={!disabled && !readonly ? handleBlur : undefined}
            />
          </Tooltip>
        </div>
      </Bulky>
      <Info {...props} />
      {error ? <CellError error={error} /> : null}
    </div>
  )
})

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: 100%;
  `,
  input: css`
    font-size: 13px;
    font-weight: inherit;
    border-width: 0;
  `,
}
